import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { orderBy, last, maxBy, map, isEmpty, get, isEqual } from 'lodash';
import SectionTitle from '@src/components/SectionTitle';
import DescriptionField from '@src/components/DescriptionField';
import { Button } from '@src/components/Button';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { CircularLoading } from '@src/components/CircularLoading';
import { trackEvent } from '@src/metrics';
import Questions from './Questions';
import './styles.scss';

const QUESTIONS_LIMIT = 5;
const getInitialQuestions = questions => {
  return !isEmpty(questions) ? orderBy(questions, 'order') : [];
};

const getQuestionsInput = (questions = []) => {
  if (isEmpty(questions)) {
    return [];
  }

  return questions.map(question => ({
    existingId: get(question, 'id') || get(question, 'existingId') || null,
    order: question.order,
    question: question.question,
  }));
};

export default class AutomatedQuestions extends PureComponent {
  static propTypes = {
    interviewQuestions: PropTypes.array,
    onFieldChange: PropTypes.func.isRequired,
    openingId: PropTypes.string.isRequired,
    saving: PropTypes.bool.isRequired,
    setPreviewQuestion: PropTypes.func.isRequired,
  };

  state = {
    questionValue: '',
    questions: getInitialQuestions(this.props.interviewQuestions),
  };

  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
    const { setPreviewQuestion } = this.props;
    const { questions } = this.state;

    if (questions.length) {
      setPreviewQuestion(questions[0]);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!isEqual(nextProps.interviewQuestions, this.props.interviewQuestions)) {
      this.setState({
        questions: nextProps.interviewQuestions,
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onQuestionValueChange = value => this.setState({ questionValue: value });

  addQuestion = () => {
    const { setPreviewQuestion, openingId } = this.props;
    const { questions: existingQuestions, questionValue } = this.state;
    const order = existingQuestions.length > 0 ? maxBy(existingQuestions, 'order').order + 1 : 1;

    if (questionValue) {
      this.setState(
        ({ questions }) => {
          return {
            questionValue: '',
            questions: [
              ...questions,
              {
                order,
                question: questionValue,
                tempId: Math.random(),
              },
            ],
          };
        },
        () => {
          const { questions } = this.state;
          this.props.onFieldChange({ interviewQuestions: getQuestionsInput(questions) });
          setPreviewQuestion(last(questions));
          trackEvent({
            context: MetricContext.CREATE_OPENING,
            interaction: MetricAction.SET_QUESTIONS,
            payload: {
              'Action Type': 'Add',
              'Opening Id': openingId,
              'Question': map(questions, 'question'),
            },
          });
        },
      );
    }
  };

  removeQuestion = question => {
    const { openingId } = this.props;
    this.setState(
      ({ questions }) => ({
        questions: questions.filter(
          ({ id, tempId, existingId }) =>
            id !== question.id || tempId !== question.tempId || existingId !== question.existingId,
        ),
      }),
      () => {
        const { questions } = this.state;
        this.props.setPreviewQuestion(last(questions));

        this.props.onFieldChange({ interviewQuestions: getQuestionsInput(questions) });
        trackEvent({
          context: MetricContext.CREATE_OPENING,
          interaction: MetricAction.SET_QUESTIONS,
          payload: {
            'Action Type': 'Remove',
            'Opening Id': openingId,
            'Question': map(questions, 'question'),
          },
        });
      },
    );
  };

  handleQuestionsReorder = questions => {
    const { openingId } = this.props;
    this.props.onFieldChange({ interviewQuestions: getQuestionsInput(questions) });
    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_QUESTIONS,
      payload: {
        'Action Type': 'Reorder',
        'Opening Id': openingId,
        'Question': map(questions, 'question'),
      },
    });
  };

  render() {
    const { setPreviewQuestion, openingId, saving } = this.props;
    const { questionValue, questions } = this.state;

    return (
      <div className={classnames('interview', saving && 'interview--saving')}>
        <div className="interview-questions">
          <SectionTitle
            size="M"
            subtitle="Add up to 5 questions that each interested applicant has to answer."
            title={
              <div className="interview__title">
                Automated Interview
                {saving && <CircularLoading className="interview__progress" size={'xx-small'} />}
              </div>
            }
          />

          {!questions ? (
            'loading...'
          ) : (
            <Questions
              onDragEnd={this.handleQuestionsReorder}
              openingId={openingId}
              questions={questions}
              removeQuestion={this.removeQuestion}
              setPreviewQuestion={setPreviewQuestion}
            />
          )}

          <div className="interview-questions-entry">
            <DescriptionField
              className="interview-questions-entry-input"
              defaultValue={questionValue}
              maxLength={120}
              onChange={this.onQuestionValueChange}
              textFieldWrapperClassName="interview-questions-entry-textfield"
              title="Add a question"
            />
          </div>

          <Button
            className="interview-questions-entry-add"
            isDisabled={!questionValue || (questions && questions.length >= QUESTIONS_LIMIT)}
            onClick={this.addQuestion}
          >
            add
          </Button>
        </div>

        <div className="interview-info">
          <div>What to ask?</div>
          <span>
            Try to be specific about applicant experience related to an open position. Use this place to ask about
            specific requirements that weren’t possible to set in the targeting step.
            <br />
            <br />
            Here are some examples:
            <br />
            <i>
              Can you describe your last project involving UX Design?
              <br />
              Tell me about your experience in Online Marketing…
              <br />
              What’s your experience with leading teams?
              <br />
              Do you speak fluent English?
            </i>
          </span>
        </div>
      </div>
    );
  }
}
